// AllStaff.js
import React, { useEffect, useState } from "react";
import { GoPlus } from "react-icons/go";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import Layout from "../../../../Components/Common/Layout";
import DashLayout from "../../../../Components/Admin/AdminDashLayout";
import SidebarSlide from "../../../../Components/Common/SidebarSlide";
import ViewStaff from "./ViewStaff";
import ProfileCard from "../SubComponents/ProfileCard";
import AddUser from "./AddUser";
import Spinner from "../../../../Components/Common/Spinner";
import CreateRole from "../../../../Components/Common/RBAC/CreateRole";
import NoDataFound from "../../../../Components/Common/NoDataFound";

import { fetchAllStaff } from "../../../../Store/Slices/Admin/Users/Staff/staff.action";
import Header from "../Component/Header";
import { getAllRolesThunk } from "../../../../Store/Slices/Common/RBAC/rbacThunks";
import useNavHeading from "../../../../Hooks/CommonHooks/useNavHeading ";
import ProtectedSection from "../../../../Routes/ProtectedRoutes/ProtectedSection";
import { PERMISSIONS } from "../../../../config/permission";
import ProtectedAction from "../../../../Routes/ProtectedRoutes/ProtectedAction";
import CardShimmer from "../../../../Components/Common/CardShimmer";

const AllStaff = () => {
  const { t } = useTranslation("admUserProfile");
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [isSidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarContent, setSidebarContent] = useState(null);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [staffData, setStaffData] = useState(null);
  const [sortOption, setSortOption] = useState(null);
  const [filterRoles, setFilterRoles] = useState([]);
  const [sortedStaff, setSortedStaff] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");

  const { staff, loading: staffLoading } = useSelector(
    (store) => store.admin.all_staff
  );
  const role = useSelector((store) => store.common.auth.role);
  const { roles: AllRoles } = useSelector((state) => state.admin.rbac);

  useEffect(() => {
    if (!staff?.length) {
      dispatch(fetchAllStaff());
      dispatch(getAllRolesThunk());
    }
  }, [staff]);

  useEffect(() => {
    let filtered = [...staff];

    // 🔍 SEARCH LOGIC
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();

      filtered = filtered.filter((member) => {
        const nameMatch = member?.fullName?.toLowerCase().includes(query);
        const emailMatch = member?.email?.toLowerCase().includes(query);

        return nameMatch || emailMatch;
      });
    }

    // 🎯 FILTER BY ROLES
    if (filterRoles.length > 0) {
      filtered = filtered.filter((member) =>
        member.position.some((pos) => filterRoles.includes(pos))
      );
    }

    // ↕️ SORT
    if (sortOption) {
      switch (sortOption) {
        case "by_date":
          filtered.sort(
            (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
          );
          break;
        case "by_roles":
          filtered.sort((a, b) => a.position.length - b.position.length);
          break;
        default:
          break;
      }
    }

    setSortedStaff(filtered);
  }, [searchQuery, sortOption, filterRoles, staff]);

  useEffect(() => {
    let filtered = [...staff];

    if (filterRoles.length > 0) {
      filtered = filtered.filter((member) =>
        member.position.some((pos) => filterRoles.includes(pos))
      );
    }

    if (sortOption) {
      switch (sortOption) {
        case "by_date":
          filtered.sort(
            (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
          );
          break;
        case "by_roles":
          filtered.sort((a, b) => a.position.length - b.position.length);
          break;
        default:
          break;
      }
    }

    setSortedStaff(filtered);
  }, [sortOption, filterRoles, staff]);

  const handleSidebarOpen = (content, data = null) => {
    setSidebarContent(content);
    setSelectedStaff(data);
    setStaffData(data);
    setSidebarOpen(true);
  };

  const handleSidebarClose = () => setSidebarOpen(false);

  const editUser = (event, data) => {
    event.stopPropagation();
    handleSidebarOpen("editStaff", data);
  };

  const handleStaffClick = (staffMember) => {
    handleSidebarOpen("viewStaff", staffMember);
  };

  const handleAddStaffClick = () => {
    handleSidebarOpen("addStaff");
    setStaffData(null);
  };

  const staffRoles =
    AllRoles?.filter(
      (dept) => dept.department.toLowerCase() === "staff"
    )?.flatMap((dept) => dept.roles) || [];

  const sortOptions = [
    { label: "By Date", value: "by_date" },
    { label: "By Roles", value: "by_roles" },
  ];

  const filterOptionsList = staffRoles.map((roleItem) => ({
    label: roleItem.name,
    value: roleItem.name,
  }));

  const handleSortFilterApply = ({ sortOption, filterOptions }) => {
    setSortOption(sortOption);
    setFilterRoles(filterOptions);
  };

  const navigateToManageRoles = (dept) => {
    navigate("/users/manage-roles", {
      state: { department: dept },
    });
  };

  const handleCreateRole = () => {
    handleSidebarOpen("createRole");
  };

  useNavHeading("Staff");

  const renderSidebarContent = () => {
    switch (sidebarContent) {
      case "viewStaff":
        return <ViewStaff staff={selectedStaff} />;
      case "addStaff":
        return <AddUser role="staff" onClose={handleSidebarClose} />;
      case "editStaff":
        return (
          <AddUser role="staff" data={staffData} onClose={handleSidebarClose} />
        );
      case "createRole":
        return <CreateRole onClose={handleSidebarClose} department="staff" />;
      default:
        return <div>{t("Select an action")}</div>;
    }
  };

  return (
    <Layout title={t("Staff | Office Flow")}>
      <DashLayout>
        <ProtectedSection
          requiredPermission={PERMISSIONS.VIEW_STAFF}
          title={"Staff"}
        >
          <div className=" relative p-4 bg-gray-50 min-h-screen">
            <Header
              title={t("All Staff")}
              count={staff?.length || 0}
              sortOptions={sortOptions}
              filterOptions={filterOptionsList}
              department="Staff"
              onSortFilterApply={handleSortFilterApply}
              navigateToManageRoles={() => navigateToManageRoles("staff")}
              handleCreateRole={handleCreateRole}
              isAdmin={role === "admin"}
              currentSort={sortOption}
              currentFilters={filterRoles}
              searchValue={searchQuery}
              onSearchChange={setSearchQuery}
            />

            {staffLoading ? (
              <div className="p-4">
                <CardShimmer columns={4} />
              </div>
            ) : (
              <div className="grid grid-cols-4 gap-4">
                {sortedStaff?.length > 0 ? (
                  sortedStaff.map((member) => (
                    <ProfileCard
                      key={member._id}
                      profile={member}
                      onClick={() => handleStaffClick(member)}
                      editUser={editUser}
                    />
                  ))
                ) : (
                  <div className="flex w-full text-gray-500 h-[90vh] items-center justify-center flex-col text-2xl">
                    <NoDataFound />
                  </div>
                )}
              </div>
            )}

            <ProtectedAction requiredPermission={PERMISSIONS.ADD_STAFF}>
              <button
                onClick={handleAddStaffClick}
                className="fixed bottom-8 end-8 btn-gradient  w-14 h-14 rounded-full flex items-center justify-center shadow-lg hover:opacity-90 transition duration-200"
                aria-label="Add New Staff"
              >
                <GoPlus className="text-2xl" />
              </button>
            </ProtectedAction>
          </div>
        </ProtectedSection>
      </DashLayout>

      <SidebarSlide
        key={sidebarContent}
        isOpen={isSidebarOpen}
        onClose={handleSidebarClose}
        title={
          <span className="bg-gradient-to-r from-pink-500 to-purple-500 inline-block text-transparent bg-clip-text">
            {sidebarContent === "viewStaff"
              ? t("Quick View of Staff")
              : sidebarContent === "createRole"
              ? t("Create New Role")
              : staffData
              ? t("Edit Staff")
              : t("Add Staff")}
          </span>
        }
        width={
          sidebarContent === "viewStaff"
            ? "50%"
            : sidebarContent === "createRole"
            ? "60%"
            : "75%"
        }
        height="100%"
      >
        {renderSidebarContent()}
      </SidebarSlide>
    </Layout>
  );
};

export default AllStaff;
